// برگه «زیبایی» پرونده بالینی: ارزیابی پوست و فیتزپاتریک (US-CLN-110) و نواحی لیزر با تعداد جلسات، آخرین پارامترها و نوبت بعد روی نقشه
// رنگی بدن (US-CLN-112؛ COMP-BODY-MAP).
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { unwrap, type ClnLaserAreaView } from '@phoenix/api-client';
import { useAccess, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ErrorState, faDigits, toJalali, toJalaliDateTime, useDisplaySettings, type DataTableColumn } from '@phoenix/ui';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { BodyMap } from './BodyMap';
import { beautyKeys, parameterSummary } from './beautyCommon';
import { clnKeys, num } from './common';
import { SkinAssessmentDialog } from './SkinAssessmentDialog';

export function BeautyProfilePanel({ patientId }: { patientId: string }) {
  const { t } = useTranslation('cln');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const { timeZone } = useDisplaySettings();
  const [recording, setRecording] = useState(false);
  const tenantId = session?.tenantId;
  const path = { params: { path: { patientId } } };
  const skin = useQuery({
    queryKey: [...clnKeys.patient(tenantId, patientId), 'skin'],
    queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/patients/{patientId}/skin-assessments', path)),
  });
  const laser = useQuery({
    queryKey: beautyKeys.laser(tenantId, patientId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/clinical/patients/{patientId}/laser-areas', path)),
  });
  const canRecord = access.accessMode === 'Full' && access.can('cln.session.record');
  const counts = Object.fromEntries((laser.data?.items ?? []).map((item) => [item.area, num(item.sessions) ?? 0]));
  const current = skin.data?.current;
  const columns: DataTableColumn<ClnLaserAreaView>[] = [
    { id: 'area', headerKey: 'cln:laser.area', cell: (row) => t(`cln:body_area.${row.area}`) },
    { id: 'sessions', headerKey: 'cln:laser.sessions', cell: (row) => faDigits(num(row.sessions) ?? 0) },
    { id: 'last', headerKey: 'cln:laser.last', cell: (row) => toJalali(row.lastAt) },
    { id: 'params', headerKey: 'cln:laser.parameters', cell: (row) => parameterSummary(row.lastParameters) || '—' },
    { id: 'next', headerKey: 'cln:laser.next', cell: (row) => (row.nextAppointmentAt ? toJalaliDateTime(row.nextAppointmentAt, { timeZone }) : '—') },
  ];
  return (
    <Stack spacing={2} data-testid="cln-beauty-profile">
      <Paper variant="outlined" sx={{ p: 1.5 }} data-testid="cln-skin">
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle1" component="h3" sx={{ flexGrow: 1 }}>
            {t('cln:skin.title')}
          </Typography>
          {canRecord && (
            <Button size="small" onClick={() => setRecording(true)} data-testid="cln-skin-add">
              {t('cln:skin.record')}
            </Button>
          )}
        </Stack>
        {skin.error ? <ErrorState error={skin.error} compact /> : null}
        {current ? (
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" data-testid="cln-skin-current">
            <Chip color="primary" label={t('cln:skin.fitzpatrick_value', { value: current.fitzpatrick })} />
            {current.skinType && <Chip variant="outlined" label={t(`cln:skin_type.${current.skinType}`)} />}
            {current.hairColor && <Chip variant="outlined" label={t(`cln:hair_color.${current.hairColor}`)} />}
            {current.hairThickness && <Chip variant="outlined" label={t(`cln:hair_thickness.${current.hairThickness}`)} />}
            <Typography variant="caption" color="text.secondary">
              {toJalaliDateTime(current.assessedAt, { timeZone })} · {current.assessedByName ?? ''}
            </Typography>
          </Stack>
        ) : (
          !skin.isPending && (
            <Typography variant="body2" color="text.secondary">
              {t('cln:skin.none')}
            </Typography>
          )
        )}
      </Paper>
      <Paper variant="outlined" sx={{ p: 1.5 }} data-testid="cln-laser-areas">
        <Typography variant="subtitle1" component="h3" sx={{ mb: 1 }}>
          {t('cln:laser.title')}
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <BodyMap counts={counts} testId="cln-laser-map" />
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            <DataTable columns={columns} rows={laser.data?.items} getRowId={(row) => row.area} isLoading={laser.isPending} error={laser.error} captionKey="cln:laser.title" emptyTitleKey="cln:laser.empty" />
          </Grid>
        </Grid>
      </Paper>
      {recording && <SkinAssessmentDialog patientId={patientId} current={current?.fitzpatrick ?? null} onClose={() => setRecording(false)} />}
    </Stack>
  );
}
